import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { useRiskScore } from '@/hooks/useRiskScore';
import { toLocalDate } from '@/utils/dateUtils';

import type { RatingSectionData, RiskOverviewPanelViewState } from '../types';

export const useRiskOverviewPanelSections = (
  riskId: string
): RiskOverviewPanelViewState => {
  const { t } = useTranslation('common', { keyPrefix: 'riskOverviewPanel' });
  const score = useRiskScore(riskId);
  const { resolveRiskRating, resolveLikelihood, resolveImpact } =
    useRiskRatingResolver('standard', { configType: 'risk_assessment' });

  const {
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
  } = score;

  const sections = useMemo((): RatingSectionData[] => {
    const formatDate = (date: null | string | undefined) =>
      date ? toLocalDate(date) : undefined;

    return [
      {
        id: 'inherent',
        title: t('inherentTitle'),
        overallRating: resolveRiskRating({
          likelihood: inherentLikelihood ?? null,
          impact: inherentImpact ?? null,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          rating: inherentRating,
        }),
        formattedDate: formatDate(inherentCompletionDate),
        rows: [
          {
            label: t('likelihoodLabel'),
            rating: resolveLikelihood(inherentLikelihood),
            formattedDate: formatDate(inherentCompletionDate),
          },
          {
            label: t('impactLabel'),
            rating: resolveImpact(inherentImpact),
            formattedDate: formatDate(inherentCompletionDate),
          },
        ],
      },
      {
        id: 'residual',
        title: t('residualTitle'),
        overallRating: resolveRiskRating({
          likelihood: residualLikelihood ?? null,
          impact: residualImpact ?? null,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          rating: residualRating,
        }),
        formattedDate: formatDate(residualCompletionDate),
        rows: [
          {
            label: t('likelihoodLabel'),
            rating: resolveLikelihood(residualLikelihood),
            formattedDate: formatDate(residualCompletionDate),
          },
          {
            label: t('impactLabel'),
            rating: resolveImpact(residualImpact),
            formattedDate: formatDate(residualCompletionDate),
          },
        ],
      },
    ];
  }, [
    t,
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    resolveRiskRating,
    resolveLikelihood,
    resolveImpact,
  ]);

  return {
    loading: score.loading,
    sections,
  };
};
